export type RemovalCause = 'expel' | 'network' | 'host-ended' | 'voluntary' | 'unknown'

const normalizeReason = (reason?: string): string => (reason ?? '').trim().toLowerCase()

export const isHostEndedSessionReason = (reason?: string): boolean => {
    const normalized = normalizeReason(reason)
    return normalized.includes('ended by host')
}

export const isSdkExpelledByHostReason = (reason?: string): boolean => {
    const normalized = normalizeReason(reason)
    return normalized.includes('kicked by host') || normalized.includes('expeled by host')
}

export const isVoluntaryLeaveReason = (reason?: string): boolean => {
    if (!reason?.trim()) {
        return false
    }

    if (isHostEndedSessionReason(reason)) {
        return false
    }

    const normalized = normalizeReason(reason)
    const voluntaryPatterns = [
        'left the meeting',
        'client close',
        'leave meeting',
        'left meeting'
    ]

    return voluntaryPatterns.some(pattern => normalized.includes(pattern))
}

const isNetworkFailureClosedReason = (reason?: string): boolean => {
    if (!reason?.trim()) {
        return false
    }

    if (isHostEndedSessionReason(reason) || isVoluntaryLeaveReason(reason)) {
        return false
    }

    const normalized = normalizeReason(reason)
    const networkPatterns = [
        'network',
        'disconnect',
        'connection lost',
        'timeout',
        'socket',
        'webrtc'
    ]

    return networkPatterns.some(pattern => normalized.includes(pattern))
}

export const isNetworkRelatedConnectionChange = (state?: string, reason?: string): boolean => {
    if (state === 'Fail') {
        return true
    }

    if (state === 'Closed') {
        return isNetworkFailureClosedReason(reason)
    }

    return false
}

export interface ClassifyLocalConnectionRemovalCauseOptions {
    localExpelPending?: boolean
}

export const classifyLocalConnectionRemovalCause = (
    state?: string,
    reason?: string,
    options: ClassifyLocalConnectionRemovalCauseOptions = {}
): RemovalCause => {
    if (options.localExpelPending) {
        return 'expel'
    }

    if (state === 'Closed' && isSdkExpelledByHostReason(reason)) {
        return 'expel'
    }

    if (state === 'Closed' && isHostEndedSessionReason(reason)) {
        return 'host-ended'
    }

    if (state === 'Reconnecting') {
        return 'network'
    }

    if (isNetworkRelatedConnectionChange(state, reason)) {
        return 'network'
    }

    if (state === 'Closed' && isVoluntaryLeaveReason(reason)) {
        return 'voluntary'
    }

    return 'unknown'
}

export interface ClassifyRemoteUserRemovedCauseOptions {
    localExpelPending?: boolean
    participantWseUserId?: string
    currentWseUserId?: string
}

export const classifyRemoteUserRemovedCause = (
    options: ClassifyRemoteUserRemovedCauseOptions = {}
): RemovalCause => {
    if (
        options.localExpelPending &&
        options.participantWseUserId &&
        options.currentWseUserId &&
        options.participantWseUserId === options.currentWseUserId
    ) {
        return 'expel'
    }

    return 'unknown'
}
